import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Dispatch, bindActionCreators } from 'redux';
import * as actions from '../../actions/generalActions';
import { RootState } from '../../reducers';
import { injectIntl, useIntl } from 'react-intl';
import * as warehouseActions from "../../actions/warehouse";
import MikaPageHeader from '../../components/MikaPageHeader/MikaPageHeader';
import MikaTable, { MikaColumn } from '../../components/MikaTable/MikaTable';
import MikaExport, { ExportColumn } from '../../components/MikaExport/MikaExport';
import PaginationComponent from '../../components/Pagination/PaginationComponent';
import '../../components/Pagination/Pagination.scss';
import '../Contact/Contact.scss';

const isMobile = window.innerWidth < 576;

interface Props {
    intl: any;
    warehouse: any;
    warehouseActions: any;
    loading: boolean;
    actions: any;
    permissions: any;
}

const WarehouseProducts = (props: Props) => {
    const intl = useIntl();
    const fm = useCallback(
        (id: string, defaultMessage: string) => intl.formatMessage({ id, defaultMessage }),
        [intl]
    );

    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        props.warehouseActions.getWarehouseProducts(null, null, currentPage, perPage, searchTerm);
    }, [currentPage, perPage]);

    const handleSearch = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
        props.warehouseActions.getWarehouseProducts(null, null, 1, perPage, value);
    };

    const data = props.warehouse?.warehouseProducts || [];
    const pagination = props.warehouse?.warehouseProductsPagination;

    const columns: MikaColumn[] = useMemo(() => [
        {
            key: 'articles',
            title: fm('warehouse.col.article', 'Artikulli'),
            sortable: true,
            render: (val: any) => val?.[0]?.label || '\u2014'
        },
        {
            key: 'quantity',
            title: fm('warehouse.purchased.quantityInStock', 'Sasia në depo'),
            sortable: true,
            render: (val: any) => val ?? '\u2014'
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const exportColumns: ExportColumn[] = useMemo(() => [
        { key: 'articles', title: fm('warehouse.col.article', 'Artikulli'), render: (r) => r.articles?.[0]?.label || '' },
        { key: 'quantity', title: fm('production.detail.quantity', 'Sasia') },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [intl.locale]);

    const fetchAllForExport = async () => data;

    return (
        <div className="clp-page">
            {!isMobile && (
                <MikaPageHeader
                    title={fm('warehouse.purchased.title', 'Materialet e blera')}
                    subtitle={pagination ? `${pagination.total} total` : undefined}
                />
            )}

            <div className="clp-compact-toolbar">
                <div className="clp-search-wrap">
                    <svg className="clp-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
                    </svg>
                    <input type="text" className="clp-search-input"
                        placeholder={fm('warehouse.purchased.searchPlaceholder', 'Kërko artikuj...')}
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="clp-search-clear" onClick={() => handleSearch('')} type="button">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
                        </button>
                    )}
                </div>
                <div className="clp-toolbar-actions">
                    <MikaExport fetchAllData={fetchAllForExport} columns={exportColumns} fileName="materialet_e_blera" title={fm('warehouse.purchased.title', 'Materialet e blera')} companyProfile={(props as any).companyProfile} />
                </div>
            </div>

            <MikaTable columns={columns} data={data} loading={props.loading} />

            <PaginationComponent
                currentPage={currentPage} perPage={perPage} pagination={pagination}
                onPageChange={(p) => setCurrentPage(p)}
                onPerPageChange={(v) => { setPerPage(v); setCurrentPage(1); }}
                label={fm('warehouse.purchased.recordsLabel', 'artikuj')}
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return { ...ownProps, ...state.app, warehouse: state.warehouse };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        warehouseActions: bindActionCreators(warehouseActions as any, dispatch),
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(WarehouseProducts));
